import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Activity, Clock, AlertTriangle, CheckCircle, TrendingUp, Users, Server, Plus, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/axios';

const API = process.env.REACT_APP_BACKEND_URL;

const severityColors = {
  P1: 'bg-red-100 text-red-700 border-red-200',
  P2: 'bg-orange-100 text-orange-700 border-orange-200',
  P3: 'bg-amber-100 text-amber-700 border-amber-200',
  P4: 'bg-blue-100 text-blue-700 border-blue-200',
};

const statusLabels = { open: 'Aberto', investigating: 'Investigando', resolved: 'Resolvido' };

export default function SLADashboard() {
  const [tab, setTab] = useState('overview');
  const [status, setStatus] = useState(null);
  const [slaReport, setSlaReport] = useState(null);
  const [history, setHistory] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [requestStats, setRequestStats] = useState(null);
  const [showNewIncident, setShowNewIncident] = useState(false);
  const [newIncident, setNewIncident] = useState({ title: '', description: '', severity: 'P3' });
  const [loading, setLoading] = useState(true);
  const [reportMonth, setReportMonth] = useState(() => {
    const now = new Date();
    return { month: now.getMonth() + 1, year: now.getFullYear() };
  });

  const fetchAll = async () => {
    try {
      const headers = { Authorization: `Bearer ${localStorage.getItem('nautilus_token')}` };
      const [statusRes, slaRes, histRes, incRes, sessRes, reqRes] = await Promise.all([
        fetch(`${API}/api/monitoring/status`, { headers }).then(r => r.json()),
        fetch(`${API}/api/monitoring/sla/report?month=${reportMonth.month}&year=${reportMonth.year}`, { headers }).then(r => r.json()),
        fetch(`${API}/api/monitoring/sla/history`, { headers }).then(r => r.json()),
        fetch(`${API}/api/monitoring/incidents?limit=50`, { headers }).then(r => r.json()),
        fetch(`${API}/api/monitoring/active-sessions`, { headers }).then(r => r.json()),
        fetch(`${API}/api/monitoring/requests/stats`, { headers }).then(r => r.json()),
      ]);
      setStatus(statusRes);
      setSlaReport(slaRes);
      setHistory(histRes.days || []);
      setIncidents(Array.isArray(incRes) ? incRes : []);
      setSessions(sessRes.active_sessions || []);
      setRequestStats(reqRes);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, [reportMonth]);
  useEffect(() => {
    const interval = setInterval(fetchAll, 30000);
    return () => clearInterval(interval);
  }, [reportMonth]);

  const handleCreateIncident = async () => {
    if (!newIncident.title.trim()) return toast.error('Título é obrigatório');
    try {
      const headers = { Authorization: `Bearer ${localStorage.getItem('nautilus_token')}`, 'Content-Type': 'application/json' };
      await fetch(`${API}/api/monitoring/incidents`, { method: 'POST', headers, body: JSON.stringify(newIncident) });
      toast.success('Incidente criado');
      setShowNewIncident(false);
      setNewIncident({ title: '', description: '', severity: 'P3' });
      fetchAll();
    } catch { toast.error('Erro ao criar incidente'); }
  };

  const handleUpdateIncident = async (id, updates) => {
    try {
      const headers = { Authorization: `Bearer ${localStorage.getItem('nautilus_token')}`, 'Content-Type': 'application/json' };
      await fetch(`${API}/api/monitoring/incidents/${id}`, { method: 'PUT', headers, body: JSON.stringify(updates) });
      toast.success('Incidente atualizado');
      fetchAll();
    } catch { toast.error('Erro ao atualizar'); }
  };

  const changeMonth = (delta) => {
    setReportMonth(prev => {
      let m = prev.month + delta;
      let y = prev.year;
      if (m < 1) { m = 12; y--; }
      if (m > 12) { m = 1; y++; }
      return { month: m, year: y };
    });
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-[400px]"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" /></div>;
  }

  const tabs = [
    { id: 'overview', label: 'Visão Geral' },
    { id: 'sla', label: 'Relatório SLA' },
    { id: 'incidents', label: 'Incidentes' },
    { id: 'sessions', label: 'Sessões Ativas' },
    { id: 'requests', label: 'Requisições' },
  ];

  return (
    <div className="space-y-6" data-testid="sla-dashboard">
      <div>
        <h1 className="text-2xl font-bold text-slate-800">Monitoramento & SLA</h1>
        <p className="text-sm text-slate-500 mt-1">Métricas de disponibilidade, performance e incidentes</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-slate-200 overflow-x-auto" data-testid="sla-tabs">
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`}
            className={`px-4 py-2 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${tab === t.id ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* TAB: Visão Geral */}
      {tab === 'overview' && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <MetricCard icon={Activity} label="Disponibilidade" value={`${status?.availability_24h?.toFixed(2)}%`} sub="Últimas 24h" ok={status?.availability_24h >= 99.5} />
            <MetricCard icon={Clock} label="Resp. Média" value={`${status?.avg_response_ms?.toFixed(0) || 0}ms`} sub={`p95: ${status?.p95_response_ms?.toFixed(0) || 0}ms`} ok={(status?.avg_response_ms || 0) < 2000} />
            <MetricCard icon={Server} label="Requisições" value={status?.total_requests_today || 0} sub={`Erros: ${status?.error_rate_percent?.toFixed(3) || 0}%`} ok={(status?.error_rate_percent || 0) < 0.1} />
            <MetricCard icon={Users} label="Usuários Ativos" value={status?.active_users || 0} sub="Últimos 15 min" />
            <MetricCard icon={AlertTriangle} label="Incidentes" value={status?.open_incidents || 0} sub="Abertos" ok={(status?.open_incidents || 0) === 0} />
          </div>
          {history.length > 0 && (
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm">Disponibilidade Diária (30 dias)</CardTitle></CardHeader>
              <CardContent>
                <div className="flex items-end gap-0.5 h-20">
                  {history.map(d => {
                    const a = d.availability || 100;
                    const c = a >= 99.5 ? 'bg-emerald-400' : a >= 97 ? 'bg-amber-400' : 'bg-red-400';
                    return <div key={d.date} className={`flex-1 ${c} rounded-t-sm hover:opacity-80`} style={{ height: `${Math.max(a, 5)}%` }} title={`${d.date}: ${a.toFixed(2)}%`} />;
                  })}
                </div>
                <div className="flex justify-between mt-1 text-xs text-slate-400">
                  <span>{history[0]?.date}</span><span>{history[history.length - 1]?.date}</span>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* TAB: Relatório SLA */}
      {tab === 'sla' && slaReport && (
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <Button variant="outline" size="sm" onClick={() => changeMonth(-1)}><ChevronLeft className="h-4 w-4" /></Button>
            <span className="text-sm font-medium text-slate-700 min-w-[100px] text-center" data-testid="sla-period">{slaReport.period}</span>
            <Button variant="outline" size="sm" onClick={() => changeMonth(1)}><ChevronRight className="h-4 w-4" /></Button>
          </div>
          <Card className={slaReport.sla_met ? 'border-emerald-200 bg-emerald-50/50' : 'border-red-200 bg-red-50/50'} data-testid="sla-compliance">
            <CardContent className="py-5">
              <div className="flex items-center gap-3">
                {slaReport.sla_met ? <CheckCircle className="h-6 w-6 text-emerald-600" /> : <AlertTriangle className="h-6 w-6 text-red-600" />}
                <div>
                  <p className={`font-semibold ${slaReport.sla_met ? 'text-emerald-700' : 'text-red-700'}`}>
                    {slaReport.sla_met ? 'SLA Cumprido' : 'SLA Violado'}
                  </p>
                  <p className="text-sm text-slate-500">
                    Disponibilidade: {slaReport.availability_percent?.toFixed(4)}% (meta: {slaReport.availability_target}%)
                    {!slaReport.sla_met && ` | Crédito: ${slaReport.credit_if_violated}`}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <MiniCard label="Downtime" value={`${slaReport.downtime_hours}h`} sub={`Máx: ${slaReport.max_allowed_downtime_hours}h`} />
            <MiniCard label="Health Checks" value={slaReport.total_health_checks} sub="No período" />
            <MiniCard label="Resp. Média" value={`${slaReport.response_time?.avg_ms}ms`} sub={`Max: ${slaReport.response_time?.max_ms}ms`} />
            <MiniCard label="Taxa de Erros" value={`${slaReport.requests?.error_rate_percent}%`} sub={`${slaReport.requests?.errors_5xx} de ${slaReport.requests?.total}`} />
          </div>
        </div>
      )}

      {/* TAB: Incidentes */}
      {tab === 'incidents' && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <Button size="sm" onClick={() => setShowNewIncident(true)} data-testid="new-incident-btn"><Plus className="h-4 w-4 mr-1" /> Novo Incidente</Button>
          </div>
          {showNewIncident && (
            <Card className="border-blue-200" data-testid="new-incident-form">
              <CardContent className="pt-4 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Novo Incidente</span>
                  <Button variant="ghost" size="sm" onClick={() => setShowNewIncident(false)}><X className="h-4 w-4" /></Button>
                </div>
                <Input placeholder="Título do incidente" value={newIncident.title} onChange={e => setNewIncident(p => ({ ...p, title: e.target.value }))} data-testid="incident-title-input" />
                <Input placeholder="Descrição" value={newIncident.description} onChange={e => setNewIncident(p => ({ ...p, description: e.target.value }))} data-testid="incident-desc-input" />
                <div className="flex gap-2">
                  {['P1', 'P2', 'P3', 'P4'].map(s => (
                    <Button key={s} variant={newIncident.severity === s ? 'default' : 'outline'} size="sm"
                      onClick={() => setNewIncident(p => ({ ...p, severity: s }))} data-testid={`severity-${s}`}>{s}</Button>
                  ))}
                </div>
                <Button size="sm" onClick={handleCreateIncident} data-testid="save-incident-btn">Criar Incidente</Button>
              </CardContent>
            </Card>
          )}
          {incidents.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-8">Nenhum incidente registrado</p>
          ) : (
            <div className="space-y-2">
              {incidents.map((inc, i) => (
                <Card key={inc.id || i} data-testid={`incident-${inc.id}`}>
                  <CardContent className="py-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-start gap-3 flex-1">
                        <Badge className={`${severityColors[inc.severity] || 'bg-slate-100'} text-xs shrink-0 mt-0.5`}>{inc.severity}</Badge>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-slate-700 truncate">{inc.title}</p>
                          {inc.description && <p className="text-xs text-slate-400 mt-0.5">{inc.description}</p>}
                          <p className="text-xs text-slate-400 mt-1">{inc.created_at ? new Date(inc.created_at).toLocaleString('pt-BR') : ''}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <Badge variant="outline" className="text-xs">{statusLabels[inc.status] || inc.status}</Badge>
                        {inc.status !== 'resolved' && (
                          <Button variant="outline" size="sm" className="text-xs h-7"
                            onClick={() => handleUpdateIncident(inc.id, { status: 'resolved' })}
                            data-testid={`resolve-${inc.id}`}>Resolver</Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* TAB: Sessões Ativas */}
      {tab === 'sessions' && (
        <div className="space-y-4">
          <Card data-testid="active-sessions-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Usuários Ativos (últimos 15 min)</CardTitle>
            </CardHeader>
            <CardContent>
              {sessions.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-6">Nenhum usuário ativo no momento</p>
              ) : (
                <div className="divide-y divide-slate-100">
                  {sessions.map((s, i) => (
                    <div key={s.user_id || i} className="flex items-center justify-between py-3" data-testid={`session-${s.user_id}`}>
                      <div className="flex items-center gap-3">
                        <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 text-xs font-bold">
                          {s.full_name?.charAt(0)?.toUpperCase() || '?'}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-slate-700">{s.full_name}</p>
                          <p className="text-xs text-slate-400">{s.email}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <Badge variant="outline" className="text-xs">{s.role}</Badge>
                        <p className="text-xs text-slate-400 mt-1">{s.request_count} req · IP {s.ip}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* TAB: Requisições */}
      {tab === 'requests' && requestStats && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <MiniCard label="Total (24h)" value={requestStats.total_requests} />
            <MiniCard label="Erros 5xx" value={requestStats.total_errors} />
            <MiniCard label="Taxa de Erros" value={`${requestStats.error_rate_percent}%`} />
            <MiniCard label="Usuários Únicos" value={requestStats.unique_users_24h} />
          </div>
          {requestStats.top_endpoints?.length > 0 && (
            <Card data-testid="top-endpoints">
              <CardHeader className="pb-2"><CardTitle className="text-sm">Top Endpoints (24h)</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {requestStats.top_endpoints.map((ep, i) => (
                    <div key={ep.path} className="flex items-center justify-between py-1.5 border-b border-slate-50 last:border-0">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="text-xs text-slate-400 w-5">{i + 1}.</span>
                        <span className="text-sm text-slate-700 truncate">{ep.path}</span>
                      </div>
                      <div className="flex items-center gap-4 shrink-0">
                        <span className="text-xs text-slate-500">{ep.avg_response_ms}ms</span>
                        <Badge variant="outline" className="text-xs">{ep.count}</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
          {requestStats.hourly?.length > 0 && (
            <Card data-testid="hourly-chart">
              <CardHeader className="pb-2"><CardTitle className="text-sm">Requisições por Hora (24h)</CardTitle></CardHeader>
              <CardContent>
                <div className="flex items-end gap-1 h-24">
                  {requestStats.hourly.map(h => {
                    const max = Math.max(...requestStats.hourly.map(x => x.count), 1);
                    return (
                      <div key={h.hour} className="flex-1 bg-blue-400 rounded-t-sm hover:bg-blue-500 transition-colors"
                        style={{ height: `${Math.max((h.count / max) * 100, 3)}%` }}
                        title={`${h.hour}: ${h.count} req (${h.errors} erros)`} />
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}

function MetricCard({ icon: Icon, label, value, sub, ok }) {
  return (
    <Card>
      <CardContent className="pt-3 pb-3">
        <div className="flex items-center gap-1.5 text-slate-500 text-xs mb-1">
          <Icon className="h-3 w-3" />{label}
        </div>
        <p className="text-xl font-bold text-slate-800">{value}</p>
        {sub && <p className="text-xs text-slate-400">{sub}</p>}
        {ok !== undefined && (
          <div className={`mt-1 h-1 w-full rounded-full ${ok ? 'bg-emerald-200' : 'bg-red-200'}`}>
            <div className={`h-1 rounded-full ${ok ? 'bg-emerald-500' : 'bg-red-500'}`} style={{ width: ok ? '100%' : '30%' }} />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function MiniCard({ label, value, sub }) {
  return (
    <Card>
      <CardContent className="pt-3 pb-3">
        <p className="text-xs text-slate-500 mb-1">{label}</p>
        <p className="text-lg font-bold text-slate-800">{value}</p>
        {sub && <p className="text-xs text-slate-400">{sub}</p>}
      </CardContent>
    </Card>
  );
}
